import { markStableUploadFile } from "./stableUploadFile.js"

const preparedImages = new WeakSet()
/**
 * Client-side image compression/resize utility (Phase 1 — Upload Pipeline Fixes).
 *
 * Runs entirely in the browser using an offscreen <canvas>. Large camera
 * photos (often 4-12MB) are downscaled and re-encoded as JPEG before they
 * ever reach the upload pipeline, which reduces upload time and the amount
 * of data that must be queued while offline (Phase 2 outbox).
 *
 * Non-image files (PDFs, docs, etc.) are returned unchanged.
 */

const DEFAULT_OPTIONS = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.75,
  // Never bother compressing a file that is already small — compression can
  // sometimes increase size for already-optimized/small images.
  skipIfSmallerThan: 500 * 1024, // 500KB
  mimeType: "image/jpeg",
}

async function loadImage(file, maxWidth) {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { resizeWidth: maxWidth, resizeQuality: "medium" })
      return { source: bitmap, width: bitmap.width, height: bitmap.height, dispose: () => bitmap.close() }
    } catch { /* Older browsers and some image formats need the image-element decoder. */ }
  }
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    const dispose = () => { img.onload = null; img.onerror = null; img.src = ""; URL.revokeObjectURL(url) }
    img.onload = () => resolve({ source: img, width: img.naturalWidth || img.width, height: img.naturalHeight || img.height, dispose })
    img.onerror = () => { dispose(); reject(new Error("Could not decode this image")) }
    img.src = url
  })
}
function canvasToFile(canvas, mimeType, quality, originalName) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas failed to produce a blob"))
          return
        }
        const newName = originalName.replace(/\.[^/.]+$/, "") + ".jpg"
        resolve(
          new File([blob], newName, {
            type: mimeType,
            lastModified: Date.now(),
          }),
        )
      },
      mimeType,
      quality,
    )
  })
}

/**
 * Compresses a single image File. Falls back to the original file on any
 * failure so a compression bug never blocks an upload outright.
 */
export async function compressImageFile(file, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  if (file && Object.keys(options).length === 0 && preparedImages.has(file)) return file

  if (!file || !file.type || !file.type.startsWith("image/")) {
    return file
  }

  // SVGs and animated GIFs don't benefit from canvas re-encoding.
  if (file.type === "image/svg+xml" || file.type === "image/gif") {
    return file
  }

  if (file.size <= opts.skipIfSmallerThan) {
    return file
  }

  let decoded
  let canvas
  try {
    decoded = await loadImage(file, opts.maxWidth)
    let { width, height } = decoded
    const ratio = Math.min(opts.maxWidth / width, opts.maxHeight / height, 1)
    width = Math.round(width * ratio)
    height = Math.round(height * ratio)

    canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext("2d")
    if (!ctx) return file
    ctx.drawImage(decoded.source, 0, 0, width, height)

    const compressed = await canvasToFile(canvas, opts.mimeType, opts.quality, file.name)

    // Only use the compressed version if it's actually smaller — otherwise
    // keep the original to avoid degrading quality for no benefit.
    const result = (opts.allowLarger || compressed.size < file.size) ? markStableUploadFile(compressed) : file
    if (Object.keys(options).length === 0) preparedImages.add(result)
    return result
  } catch (error) {
    console.log("[v0] Image compression failed, using original file:", error?.message)
    return file
  } finally {
    decoded?.dispose()
    if (canvas) { canvas.width = 0; canvas.height = 0 }
  }
}

/**
 * Compresses a list/array of Files sequentially, preserving order and limiting decode memory.
 */
export async function compressImageFiles(files, options = {}) {
  const list = Array.from(files || [])
  const results = []
  for (const file of list) results.push(await compressImageFile(file, options))
  return results
}
